import { faSearch, faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cx from 'classnames';
import { BZ2_SEARCH_BUTTON, BZ2_SEARCH_IN_ALL } from 'mk/autogenerated/translations/TextSearch.deed9abd49e0f3f2e051745cdcd4ba91'
import ClearableInput from 'mk2/components/ClearableInput';
import Form from 'mk2/components/Form';
import useEventCallback from 'mk2/hooks/useEventCallback';
import React, { useState } from 'react';
import styles from './TextSearch.mscss';

interface OwnProps {
    className?: string;
    value?: string;
    placeholder?: string;
    hideButtonUntilFocused?: boolean;
    outlined?: boolean;
    buttonLabel?: React.ReactNode;

    onChange?(value: string);
}

type Props = OwnProps;

const TextSearch: React.FunctionComponent<Props> = ({
    className,
    value = '',
    onChange,
    placeholder = BZ2_SEARCH_IN_ALL,
    hideButtonUntilFocused = false,
    outlined = false,
    buttonLabel,
}) => {
    const [showButton, setShowButton] = useState(!hideButtonUntilFocused);
    const [active, setActive] = useState(false);
    const [localValue, setLocalValue] = useState(value);
    const [previousValue, setPreviousValue] = useState(value);

    if (previousValue !== value) {
        // getDerivedStateFromProps
        setLocalValue(value);
        setPreviousValue(value);
    }

    const onFocus = useEventCallback(
        (event: React.FocusEvent<HTMLInputElement>) => {
            setActive(true);
            setShowButton(true);
        },
        [setActive, setShowButton],
    );

    const onBlur = useEventCallback(
        (event: React.FocusEvent<HTMLInputElement>) => {
            setActive(false);
        },
        [setActive],
    );

    const onLocalChange = useEventCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            setLocalValue(event.currentTarget.value);
        },
        [setLocalValue],
    );

    const onClear = useEventCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault();
            setLocalValue('');
            if (onChange) {
                onChange('');
            }
        },
        [setLocalValue, onChange],
    );

    const onSubmit = useEventCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (onChange) {
                onChange(localValue);
            }
        },
        [onChange, localValue],
    );

    return (
        <Form className={cx(styles.TextSearch, className)} onSubmit={onSubmit}>
            <ClearableInput
                className={cx(
                    styles.TextSearch__input,
                    active && styles['TextSearch__input--active'],
                    value && styles['TextSearch__input--hasValue'],
                )}
                icon={<FontAwesomeIcon icon={faSearch} flip="horizontal" />}
                clearIcon={<FontAwesomeIcon icon={faTimesCircle} className={styles.TextSearch__clearIcon} />}
                hasValue={Boolean(value)}
                onClear={onClear}
                outlined={outlined}
            >
                <input
                    className={styles.TextSearch__input__input}
                    type="text"
                    onFocus={onFocus}
                    onBlur={onBlur}
                    onChange={onLocalChange}
                    placeholder={placeholder}
                    value={localValue}
                />
            </ClearableInput>
            {showButton && <button className={styles.TextSearch__submit}>{buttonLabel || BZ2_SEARCH_BUTTON}</button>}
        </Form>
    );
};

TextSearch.displayName = 'TextSearch';

export default React.memo<Props>(TextSearch);
